import * as React from 'react';

import { ReportTypes } from '@showbie-socrative/socrative-utils';
import { sendDownloadInitiateMessage } from '~showbie/messages/sendDownloadMessage';

import { useTeacherApplicationContext } from '~shared/context/TeacherApplicationContext';
import { ReportTypeContext } from '~teacher/components/ExportReports/ReportTypeContext';
import { useReportAnalytics } from '~teacher/components/QuizResultsHeader/ReportAnalyticsContext';

import { downloadReports } from '../../../../api/reports/reportsApi';
import { ExportFeedbackRetryError } from '../ExportFeedback/ExportFeedback';
import {
  AssessmentsExportFeedbackDownloadComplete,
  AssessmentsExportFeedbackDownloadInProgress,
} from '../ExportFeedback/ExportFeedbackDownload';
import { ReportOptionType } from '../ReportOption';
import { ReportOptionHeader } from '../ReportOption/ReportOptionHeader';
import {
  useOnPrepareDownloadCallback,
  useReportReducer,
} from '../ReportOption/ReportOptionHook';
import {
  ExportView,
  ReportOptionState,
  toForm,
} from '../ReportOption/ReportOptionHookReducer';
import { AssessmentsReportOption } from './AssessmentsReportOption';

export interface AssessmentsCommonProps {
  activityInstanceId: number;
  initialOptions: ReportOptionState;
  downloadReports: typeof downloadReports;
  canCustomizeExportResults?: boolean;
  onExportingChange?: (exporting: boolean) => void;
}

interface ReportSpecificProps {
  reportOptions: ReportOptionType[];
  initialOptions: ReportOptionState;
  reportRequestSelectedType: ReportTypes;
  description: string;
  title: string;
}

type Props = AssessmentsCommonProps & ReportSpecificProps;

export function AssessmentsExportReportsPanel({
  activityInstanceId,
  downloadReports,
  reportOptions,
  initialOptions,
  reportRequestSelectedType,
  description,
  title,
  onExportingChange,
}: Props) {
  const { dispatch, state } = useReportReducer({
    reportOptions,
    initialOptions,
  });
  const onBackToOptionSettings = () => toForm(dispatch)();
  const reportAnalytics = useReportAnalytics();
  const { exportView, optionState, error, isFetching, preparedUrl } = state;
  const appContext = useTeacherApplicationContext();

  // Inform parent whenever we enter/exit the export flow
  React.useEffect(() => {
    onExportingChange?.(exportView !== ExportView.FORM);
  }, [exportView, onExportingChange]);

  const prepareDownload = useOnPrepareDownloadCallback({
    activityInstanceId,
    reportAnalytics,
    reportRequestSelectedType,
    optionState,
    dispatch,
    appContext,
    downloadReports,
  });

  const handlePrepareDownload = React.useCallback(() => {
    prepareDownload();
  }, [prepareDownload]);

  const handleDownload = () => {
    if (!preparedUrl) {
      console.error('No download URL available');
      return;
    }

    sendDownloadInitiateMessage(preparedUrl);
  };

  switch (exportView) {
    case ExportView.FORM:
      return (
        <ReportTypeContext.Provider value={reportRequestSelectedType}>
          <AssessmentsReportOption
            isFetching={isFetching}
            onPrepareDownload={handlePrepareDownload}
            reportOptions={reportOptions}
            optionState={optionState}
            header={
              <ReportOptionHeader
                description={description}
                title={title}
                shouldShowProBadge={false}
                shouldHaveSpaceBeforeTitle={true}
              />
            }
            dispatch={dispatch}
          />
        </ReportTypeContext.Provider>
      );
    case ExportView.PREPARING:
      return (
        <AssessmentsExportFeedbackDownloadInProgress
          onCancel={onBackToOptionSettings}
        />
      );
    case ExportView.DOWNLOADING:
      return (
        <AssessmentsExportFeedbackDownloadComplete
          onDownload={handleDownload}
        />
      );
    case ExportView.ERROR:
      return (
        <ExportFeedbackRetryError
          error={error}
          onRetry={onBackToOptionSettings}
        />
      );
    default:
      throw new Error('unreachable code!');
  }
}
